import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
import { Button } from 'src/@/components/ui/button';
import Iconify from 'src/components/Iconify';
import { toast } from 'src/@/components/ui/use-toast';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import v2_actionCriarCanal from 'src/actions/v2/cliente/canais/v2_actionCriarCanal';
import v2_actionMetaVincularContaSocial from 'src/actions/v2/meta/v2_actionMetaVincularContaSocial';
import ChannelCreationForm from '../canais/ChannelCreationForm';

// Espelha PROVIDER_POR_PLATFORM do backend (v2_meta.controller.js), na direcao
// inversa: aqui partimos da conta social (provider) e queremos a plataforma
// de canal correspondente. So estes tres providers aceitam vinculo — Mercado
// Livre e Ads usam outro caminho (ou nenhum), ver comentario em index.js.
export const CANAL_PLATFORM_POR_PROVIDER = {
    facebook: 'messenger',
    instagram: 'instagram',
    whatsapp_business: 'whatsapp_business',
};

const PLATFORM_ICON = {
    messenger: 'ic:round-facebook',
    instagram: 'mdi:instagram',
    whatsapp_business: 'ic:round-whatsapp',
};

/**
 * Painel do "final do onboarding": a conta social acabou de conectar (ou o
 * usuario clicou em "Vincular canal" num card existente) e precisa de um
 * canal para as mensagens chegarem. Oferece os canais já livres da mesma
 * plataforma, ou — se nenhum existir — cria um em 2 passos (nome +
 * direcionamento, a plataforma já é conhecida) e vincula na sequência.
 */
export default function LinkChannelDialog({ account, clienteId, userId, position = 1, total = 1, onClose, onLinked }) {
    const { t } = useTranslation('settings');
    const open = Boolean(account);
    const platform = account ? CANAL_PLATFORM_POR_PROVIDER[account.provider] : null;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [channels, setChannels] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [creating, setCreating] = useState(false);
    const [linking, setLinking] = useState(false);

    useEffect(() => {
        if (!open || !account) return undefined;
        let cancelled = false;
        setLoading(true);
        setError('');
        setSelectedId('');
        setShowCreateForm(false);
        // O componente fica montado entre uma conta e a proxima da fila —
        // sem isto, "creating"/"linking" de uma conta anterior vazariam para
        // a proxima e travariam o botao de submit sem motivo.
        setCreating(false);
        setLinking(false);
        actionListaCanaisPorCliente(clienteId)
            .then((res) => {
                if (cancelled) return;
                const list = (res?.data?.data || []).filter(
                    (c) => c.platform === platform && !c.descontinuado && !c.mkt_account_id
                );
                setChannels(list);
                setShowCreateForm(list.length === 0);
            })
            .catch(() => {
                if (!cancelled) setError(t('socialAccounts.linkChannel.loadError'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, account?._id, clienteId]);

    async function vincular(canalId) {
        setLinking(true);
        setError('');
        try {
            await v2_actionMetaVincularContaSocial(canalId, { mkt_account_id: account._id, user_id: userId });
            toast({ title: t('socialAccounts.linkChannel.linkedTitle') });
            onLinked?.();
        } catch (err) {
            setError(err?.response?.data?.mensagem || t('socialAccounts.linkChannel.linkError'));
        } finally {
            setLinking(false);
        }
    }

    async function handleCreateAndLink(data) {
        setCreating(true);
        setError('');
        try {
            const response = await v2_actionCriarCanal(clienteId, {
                scope: { nome: data.nome, platform, webhook_type: data.webhook_type },
            });
            const novoCanalId = response.data.data._id;
            await vincular(novoCanalId);
        } catch (err) {
            setError(err?.response?.data?.mensagem || t('channels.creationForm.createError'));
        } finally {
            setCreating(false);
        }
    }

    if (!account) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="sm:max-w-lg w-[95vw] p-0 overflow-hidden border-0 shadow-2xl rounded-2xl flex flex-col max-h-[85vh]">
                <DialogHeader className="px-6 py-5 border-b border-border text-left flex-shrink-0">
                    <div className="flex items-center gap-3 pr-8">
                        <div className="w-11 h-11 rounded-2xl bg-violet-50 dark:bg-violet-500/15 text-[#7C3AED] flex items-center justify-center flex-shrink-0">
                            <Iconify icon="eva:link-2-outline" width={22} />
                        </div>
                        <div className="min-w-0">
                            <DialogTitle className="text-lg font-bold text-foreground">
                                {t('socialAccounts.linkChannel.title')}
                            </DialogTitle>
                            <p className="text-sm text-muted-foreground mt-0.5 truncate">
                                {t('socialAccounts.linkChannel.subtitle', { name: account.account_name })}
                            </p>
                        </div>
                    </div>
                    {total > 1 && (
                        <span className="text-xs text-muted-foreground mt-2 inline-block">
                            {t('socialAccounts.linkChannel.position', { position, total })}
                        </span>
                    )}
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-14 text-muted-foreground gap-2">
                        <Iconify icon="eos-icons:loading" width={22} />
                        <span className="text-sm">{t('socialAccounts.linkChannel.loading')}</span>
                    </div>
                ) : showCreateForm ? (
                    <div className="flex-1 min-h-0 flex flex-col">
                        {channels.length > 0 && (
                            <button
                                type="button"
                                className="text-xs text-[#7C3AED] hover:underline mx-6 mt-4 inline-flex items-center gap-1 self-start"
                                onClick={() => setShowCreateForm(false)}
                            >
                                <Iconify icon="eva:arrow-back-outline" width={13} />
                                {t('socialAccounts.linkChannel.backToList')}
                            </button>
                        )}
                        {error && (
                            <div className="mx-6 mt-4 flex items-start gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/25 rounded-xl p-3">
                                <Iconify icon="eva:alert-circle-outline" width={16} className="mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                        {/* Sem altura fixa: o "Direcionamento" tem 7 cards e o "Nome" so um
                            input — um px fixo (era h-[420px]) ou sobrava vazio num passo ou
                            cortava o outro, escondendo o rodape com o botao Continuar sem
                            scroll pra compensar. `flex-1 overflow-hidden min-h-0` deixa o
                            ChannelCreationForm (que ja e "h-full flex-col" com seu proprio
                            corpo rolavel e rodape fixo) ocupar exatamente o espaco que o
                            DialogContent (max-h-[85vh]) tiver disponivel — mesmo padrao do
                            DialogNewChannel.js, o outro lugar que usa este componente. */}
                        <div className="flex-1 min-h-0 overflow-hidden">
                            <ChannelCreationForm
                                lockedPlatform={platform}
                                canal={{ nome: account.account_name }}
                                onSubmit={handleCreateAndLink}
                                onChange={() => {}}
                                onCancel={() => (channels.length > 0 ? setShowCreateForm(false) : onClose())}
                                isSubmitting={creating || linking}
                                submitError=""
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="px-6 py-5 flex-1 overflow-y-auto min-h-0">
                            {error && (
                                <div className="mb-4 flex items-start gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/25 rounded-xl p-3">
                                    <Iconify icon="eva:alert-circle-outline" width={16} className="mt-0.5 flex-shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                                {t('socialAccounts.linkChannel.existingChannels')}
                            </p>
                            <div className="space-y-2">
                                {channels.map((c) => (
                                    <button
                                        key={c._id}
                                        type="button"
                                        onClick={() => setSelectedId(c._id)}
                                        className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
                                            selectedId === c._id
                                                ? 'border-[#7C3AED] bg-violet-50 dark:bg-violet-500/10'
                                                : 'border-border hover:bg-muted/50'
                                        }`}
                                    >
                                        <div className="w-9 h-9 rounded-lg bg-violet-50 dark:bg-violet-500/15 text-[#7C3AED] flex items-center justify-center flex-shrink-0">
                                            <Iconify icon={PLATFORM_ICON[platform]} width={18} />
                                        </div>
                                        <span className="text-sm font-medium text-foreground truncate">{c.nome}</span>
                                        {selectedId === c._id && (
                                            <Iconify icon="eva:checkmark-circle-2-fill" width={18} className="text-[#7C3AED] ml-auto flex-shrink-0" />
                                        )}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                className="text-xs text-[#7C3AED] hover:underline inline-flex items-center gap-1 mt-4"
                                onClick={() => setShowCreateForm(true)}
                            >
                                <Iconify icon="eva:plus-circle-outline" width={14} />
                                {t('socialAccounts.linkChannel.createInstead')}
                            </button>
                        </div>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30 flex-shrink-0">
                            <Button type="button" variant="ghost" onClick={onClose} disabled={linking} className="rounded-xl">
                                {t('socialAccounts.linkChannel.skip')}
                            </Button>
                            <Button
                                type="button"
                                onClick={() => vincular(selectedId)}
                                disabled={!selectedId || linking}
                                className="rounded-xl px-6 bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
                            >
                                {linking && <Iconify icon="eos-icons:loading" width={15} className="mr-1.5" />}
                                {t('socialAccounts.linkChannel.linkButton')}
                            </Button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
